import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { WIZARD_STATUS_COMPLETE } from 'platform/site-wide/wizard';

import SubTaskContainerHeader from '../SubTaskContainerHeader';
import useSubtaskStatusMarker from '../useSubtaskStatusMarker';
import { useSyncFormBddState } from '../bddFormData';
import { usePathHistory } from '../utils/tracking/PathHistoryContext';
import {
  trackScreenerStepView,
  trackEvidenceGuidanceDisplayed,
  trackScreenerCompleted,
} from '../../utils/tracking/features/screenerTracking';
import { RESUME_PAGE } from '../../constants';
import { LAY_STATEMENT_FORM_URL } from '../../../shared/constants/entryConstants';
import pageNames from './pageNames';

const pactDataKey = 'view:pactEligibility';
const claimTypeKey = 'view:claimType';
const militaryStatusKey = 'view:militaryStatus';

// Users who may be eligible for PACT Act benefits see an extra paragraph about
// providing exposure history during the application.
const showsPactContent = data =>
  data[pactDataKey] === 'yes' || data[pactDataKey] === 'not-sure';

// A claim-for-increase-only (CFI) claim shows a reduced evidence list focused
// on showing a worsening condition; new and combined claims show the full list.
const isIncreaseOnly = data => data[claimTypeKey] === 'increase';

/**
 * Start 3 — Evidence needed. End of the screener; Continue hands off into the
 * 526EZ application. Content varies by claim type and PACT Act eligibility.
 * @param {Object} data - subtask data
 * @returns {JSX}
 */
const EvidenceNeededPage = ({ data = {} }) => {
  const { addStep, getHistory } = usePathHistory();

  // Marking the screener complete lets the platform save-in-progress resume
  // route load the form when the veteran continues into the application.
  useSubtaskStatusMarker(WIZARD_STATUS_COMPLETE);

  // A standard claim: keep a separation date only for active duty.
  useSyncFormBddState(data, false);

  useEffect(() => {
    trackScreenerStepView({
      stepName: pageNames.evidenceNeeded,
      pathHistoryContext: { addStep, getHistory },
    });
    trackEvidenceGuidanceDisplayed({
      claimType: data[claimTypeKey],
      pactEligibility: data[pactDataKey],
      showsPactContent: showsPactContent(data),
      evidenceListVariant: isIncreaseOnly(data) ? 'increase-only' : 'full',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div id={pageNames.evidenceNeeded}>
      <SubTaskContainerHeader title="Start your disability compensation claim" />
      <p>
        Next, you’ll fill out your application. You’ll have the opportunity to
        share information about yourself and your service.
      </p>
      {showsPactContent(data) ? (
        <p>
          We’ll ask you to describe the conditions you’re claiming, include
          supporting evidence, and review everything before submitting. This
          includes conditions you’re claiming under the PACT Act.
        </p>
      ) : (
        <p>
          We’ll ask you to describe the conditions you’re claiming, include
          supporting evidence, and review everything before submitting.
        </p>
      )}
      <va-accordion open-single>
        <va-accordion-item
          bordered
          header="Evidence you’ll need to support your claim"
          id="supporting-evidence"
        >
          {isIncreaseOnly(data) ? (
            <>
              <p className="vads-u-margin-top--0">
                You’ll need to submit current evidence from a medical
                professional that shows your disability has gotten worse.
              </p>
              <p>Submit at least one of these types of documents:</p>
              <ul>
                <li>
                  <strong>Medical records.</strong> Medical records help us
                  understand the current severity of your condition. These
                  records also help us confirm your diagnoses. Medical records
                  can be from a VA medical center, private doctor, or both.
                </li>
                <li>
                  <strong>Buddy or lay statements.</strong> Buddy or lay
                  statements are formal statements from someone who knows you
                  and can describe how your condition affects your daily life. A
                  spouse, caregiver, or fellow service member can submit these
                  statements. Buddy statements can be a written statement which
                  you upload directly with your claim, or they can be submitted
                  using{' '}
                  <va-link
                    external
                    href={LAY_STATEMENT_FORM_URL}
                    text="VA Form 21-10210 Lay/Witness Statement"
                  />
                  .
                </li>
              </ul>
            </>
          ) : (
            <>
              <p className="vads-u-margin-top--0">
                You can submit any of these types of evidence to support your
                claim:
              </p>
              <ul>
                <li>
                  <strong>Medical records.</strong> Medical records help us
                  understand the current severity of your condition. These
                  records also help us confirm your diagnoses. Medical records
                  can be from a VA medical center, private doctor, or both.
                </li>
                <li>
                  <strong>Buddy or lay statements.</strong> Buddy or lay
                  statements are formal statements from someone who knows you
                  and can describe how your condition affects your daily life. A
                  spouse, caregiver, or fellow service member can submit these
                  statements. Buddy statements can be a written statement which
                  you upload directly with your claim, or they can be submitted
                  using{' '}
                  <va-link
                    external
                    href={LAY_STATEMENT_FORM_URL}
                    text="VA Form 21-10210 Lay/Witness Statement"
                  />
                  .
                </li>
                <li>
                  <strong>Discharge or separation papers.</strong> Your
                  discharge or separation papers confirm your period of service.
                  These papers also confirm your eligibility to get VA benefits.
                  You can submit your DD214 or other separation documents.
                </li>
                <li>
                  <strong>Service treatment records.</strong> Service treatment
                  records can help us connect your condition to your military
                  service.
                </li>
              </ul>
            </>
          )}
          <p>
            <strong>Note:</strong> If your medical records, treatment notes, or
            test results are from a VA medical center or clinic, we’ll get those
            documents for you.
          </p>
          <p>
            If your documents are from a non-VA or private facility, you can
            give us permission to request them on your behalf.
          </p>
        </va-accordion-item>
      </va-accordion>
    </div>
  );
};

EvidenceNeededPage.propTypes = {
  data: PropTypes.shape({}),
};

export default {
  name: pageNames.evidenceNeeded,
  component: EvidenceNeededPage,
  back: pageNames.pactEligibility,
  // Route the veteran straight into Form 526EZ through the platform
  // save-in-progress resume route, which loads their prefill/in-progress form
  // and hands off directly into the application without re-showing the intro.
  next: () => RESUME_PAGE,
  onContinue: data =>
    trackScreenerCompleted({
      pageName: pageNames.evidenceNeeded,
      outcome: 'standard',
      militaryStatus: data[militaryStatusKey],
      claimType: data[claimTypeKey],
      pactEligibility: data[pactDataKey],
    }),
};
